'use client';

import { isChunkLoadError, recoverFromChunkLoadError } from '@/utils/chunkLoadErrorGuard';
import * as Sentry from '@sentry/nextjs';
import { useEffect, useState } from 'react';

export default function GlobalError({ error, reset }: { error: Error & { digest?: string }; reset: () => void }) {
  // See app/error.tsx — deployment-skew chunk errors need a reload, not reset().
  const isChunkError = isChunkLoadError(error);
  const [reloading, setReloading] = useState(false);

  useEffect(() => {
    if (isChunkError) {
      if (recoverFromChunkLoadError(error, 'global-error-boundary')) {
        setReloading(true);
      }
      return;
    }
    Sentry.captureException(error);
  }, [error, isChunkError]);

  if (reloading) {
    return (
      <html lang='en'>
        <body>
          <p
            style={{
              display: 'flex',
              minHeight: '100vh',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: 'Inter, system-ui, sans-serif',
              color: '#6b7280',
            }}
            data-testid='global-error-reloading'
          >
            A new version is available. Reloading…
          </p>
        </body>
      </html>
    );
  }

  return (
    <html lang='en'>
      <body>
        <div
          style={{
            display: 'flex',
            minHeight: '100vh',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'Inter, system-ui, sans-serif',
          }}
        >
          <div
            style={{
              width: 480,
              maxWidth: '90%',
              borderRadius: 10,
              backgroundColor: '#fff',
              padding: 24,
              boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
            }}
          >
            <span style={{ fontSize: 24, fontWeight: 700, color: '#dc2626', marginBottom: 8 }}>
              Oops! Something went wrong.
            </span>

            <p
              style={{
                color: '#6b7280',
                marginBottom: 24,
                maxHeight: '50vh',
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
              }}
            >
              {error?.message || 'An unexpected error occurred.'}
            </p>

            <div style={{ display: 'flex', justifyContent: 'center', gap: 16, width: '100%' }}>
              <button
                style={{
                  borderRadius: 8,
                  backgroundColor: '#A1A1AA',
                  padding: '8px 16px',
                  color: '#fff',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 14,
                  flex: 1,
                  height: 40,
                }}
                onClick={() => {
                  window.open('https://t.me/universalx_support_bot', '_blank');
                }}
                data-testid='global-error-button-1'
              >
                User Support
              </button>

              <button
                style={{
                  borderRadius: 8,
                  backgroundColor: '#D745FF',
                  padding: '8px 16px',
                  color: '#fff',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 14,
                  flex: 1,
                  height: 40,
                }}
                onClick={isChunkError ? () => window.location.reload() : reset}
                data-testid='global-error-button-2'
              >
                {isChunkError ? 'Refresh' : 'Try Again'}
              </button>
            </div>
          </div>
        </div>
      </body>
    </html>
  );
}
